import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from 'react-i18next'
import { getButtonTestId } from '../Buttons/getButtonTestId'

/** @notExported */
interface EditButtonProps {
  /** Action to perform when button is clicked. */
  clickAction: () => void
  /** Tooltip to display. */
  tooltip?: string
  /** Whether or not the button is valid. */
  valid?: boolean
  /** Size of the button. */
  size?: 'medium' | 'small' | undefined | 'xsmall'
  /** Whether or not to float the button. */
  noFloat?: boolean
  /** Test id of the button. */
  testId?: string
}

/**
 * Edit button component.
 *
 * @returns Edit button.
 * @notExported
 * @category Buttons
 */
const EditButton: React.FC<EditButtonProps> = ({
  clickAction,
  tooltip,
  valid = true,
  size = 'small',
  noFloat = false,
  testId,
}) => {
  const { t } = useTranslation()
  const label = tooltip ?? (t('edit') as string)
  const resolvedTestId = getButtonTestId({ fallback: 'button-edit-icon', label, testId })

  const getButton = () => {
    return (
      <span style={{ display: 'inline-flex', float: noFloat ? 'none' : 'right' }}>
        <IconButton
          data-testid={resolvedTestId}
          aria-label={label}
          onClick={event => {
            event.stopPropagation()
            clickAction()
          }}
          disabled={!valid}
          size={size === 'xsmall' ? 'medium' : size}
        >
          <EditIcon fontSize={size === 'xsmall' ? 'small' : undefined} />
        </IconButton>
      </span>
    )
  }
  // MUI Tooltip requires a wrapper for disabled button children.
  return (
    <Tooltip title={label} describeChild>
      {getButton()}
    </Tooltip>
  )
}

export default EditButton
